import { useState, useCallback, useRef, useEffect } from "react";
import axios, { type AxiosProgressEvent } from "axios";
import { v4 as uuidv4 } from "uuid";
import { getAuthHeader } from "../utils/authHeader";
import {
  VIDEO_SERVICE_UNAVAILABLE_CODE,
  VIDEO_SERVICE_UNAVAILABLE_MESSAGE,
} from "../constants/video-service";
import {
  type FileUploadState,
  type UploadConfig,
  UploadStatus,
  UploadPhase,
  type UploadQueueState,
} from "../components/listings/types";
import type { MediaType } from "../types/mediaType";
import { ListingType } from "../types/userListing";
import type { Images } from "../types/listingImages";
import type { ListingVideos } from "../types/listingVideos";
import { toast } from "react-toastify";

interface UseParallelUploadProps {
  type: MediaType;
  listingId?: string;
  listingType?: ListingType;
  propertyAddress1?: string;
  assetType?: string;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onFileComplete?: () => void;
  onFileError?: (fileId: string, error: string) => void;
  onImageTagLimitExceeded?: () => void;
  existingMedia?: { images?: Images[]; videos?: ListingVideos[] }; // Existing media to show as completed
  /**
   * Optional replacement for the built-in /services/check-video step.
   *
   * When provided, this callback runs instead of the default backend
   * validation after the file finishes uploading. Used by flows that need
   * different validation rules and no /services/check-video side effects
   * (e.g. ZapCap captioned video — applies ZAPCAP_POLICY via /services/probe-video
   * and cleans up the GCS object on failure).
   *
   * Return `{ valid: false, reason }` to fail the upload; the hook will throw
   * with that reason and surface it through the normal failure path.
   */
  customVideoValidator?: (
    filename: string,
    origName: string
  ) => Promise<VideoValidationResult>;
}

interface VideoValidationResult {
  id: string;
  name: string;
  valid: boolean;
  reason?: string;
  needsConversion?: boolean;
  thumbnail?: string;
}

const DEFAULT_CONFIG: UploadConfig = {
  maxConcurrentImageUploads: parseInt(
    (process.env.REACT_APP_MAX_CONCURRENT_IMAGE_UPLOADS || "3").trim()
  ),
  maxConcurrentVideoUploads: parseInt(
    (process.env.REACT_APP_MAX_CONCURRENT_VIDEO_UPLOADS || "1").trim()
  ),
  maxRetries: 3,
  retryDelayBase: 1000,
  enableParallelUploads: true,
};

const MAX_VIDEO_SIZE = 2 * 1024 ** 3; // 2 GB in bytes
const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25 MB in bytes

const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "mkv", "webm", "avi", "matroska"]);

const VIDEO_MIME_TYPES: Record<string, string> = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  mkv: "video/x-matroska",
  webm: "video/webm",
  avi: "video/x-msvideo",
  matroska: "video/x-matroska",
};

// Some browsers (Chrome on macOS, Firefox) don't populate file.type for
// certain containers (e.g. .mkv). Fall back to extension detection.
const isVideoFile = (file: File): boolean => {
  if (file.type.startsWith("video/")) return true;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return VIDEO_EXTENSIONS.has(ext);
};

// Returns file.type if set, otherwise infers MIME type from extension.
const getFileContentType = (file: File): string => {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return VIDEO_MIME_TYPES[ext] ?? "";
};

export const useParallelUpload = ({
  type,
  listingId,
  listingType,
  propertyAddress1,
  assetType,
  onUploadStart,
  onUploadComplete,
  onFileComplete,
  onFileError,
  onImageTagLimitExceeded,
  existingMedia,
  customVideoValidator,
}: UseParallelUploadProps) => {
  const [queueState, setQueueState] = useState<UploadQueueState>({
    files: [],
    activeImageUploads: 0,
    activeVideoUploads: 0,
    completedUploads: 0,
    failedUploads: 0,
    totalProgress: 0,
  });

  const [config] = useState<UploadConfig>(DEFAULT_CONFIG);
  const processingFiles = useRef<Set<string>>(new Set());
  // MB-1396: one "video service unavailable" notice per batch. Reset when a new
  // batch is queued, so a later retry after the outage clears still notifies.
  const serviceUnavailableNotifiedRef = useRef(false);
  const processQueueTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const filesRef = useRef<FileUploadState[]>([]);
  const processQueueRef = useRef<() => void>();
  const completionNotifiedRef = useRef<boolean>(false);
  const initializedExistingMedia = useRef<boolean>(false);
  const MAX_UPLOAD_RETRIES = 2;

  // Initialize existing media as completed files
  useEffect(() => {
    if (existingMedia && !initializedExistingMedia.current) {
      const existingFiles: FileUploadState[] = [];
      
      // Add existing images
      if (existingMedia.images && existingMedia.images.length > 0) {
        existingMedia.images.forEach((image: Images) => {
          // Create a mock File object for display purposes
          const mockFile = new File([], image.name || 'image', { type: 'image/jpeg' });
          existingFiles.push({
            id: image.id || uuidv4(),
            file: mockFile,
            preview: image.url || '',
            progress: 100,
            status: UploadStatus.COMPLETED,
            phase: UploadPhase.PERSISTENCE,
            uploadedUrl: image.url,
            retryCount: 0,
            isExisting: true, // Mark as existing so we don't try to delete/retry
            filename: image.name || image.id, // Add filename for proper deletion
          });
        });
      }
      
      // Add existing videos
      if (existingMedia.videos && existingMedia.videos.length > 0) {
        existingMedia.videos.forEach((video: ListingVideos) => {
          const mockFile = new File([], video.name || 'video', { type: 'video/mp4' });
          existingFiles.push({
            id: video.id || uuidv4(),
            file: mockFile,
            preview: video.thumbnail || video.url || '',
            progress: 100,
            status: UploadStatus.COMPLETED,
            phase: UploadPhase.PERSISTENCE,
            uploadedUrl: video.url,
            retryCount: 0,
            isExisting: true,
            filename: video.name || video.id, // Add filename for proper deletion
          });
        });
      }
      
      if (existingFiles.length > 0) {
        setQueueState((prev) => ({
          ...prev,
          files: [...existingFiles, ...prev.files],
          completedUploads: existingFiles.length,
        }));
      }
      
      initializedExistingMedia.current = true;
    }
  }, [existingMedia]);

  useEffect(() => {
    filesRef.current = queueState.files;
  }, [queueState.files]);

  // Get current file state - this ensures we always have the latest state
  const getCurrentFileState = useCallback(
    (fileId: string): FileUploadState | undefined => {
      return queueState.files.find((f) => f.id === fileId);
    },
    [queueState.files]
  );

  // Calculate overall progress
  useEffect(() => {
    const totalFiles = queueState.files.length;
    if (totalFiles === 0) {
      setQueueState((prev) => ({ ...prev, totalProgress: 0 }));
      return;
    }

    const totalProgress =
      queueState.files.reduce((sum, file) => sum + file.progress, 0) /
      totalFiles;
    setQueueState((prev) => ({ ...prev, totalProgress }));
  }, [queueState.files]);

  const updateFileState = useCallback(
    (fileId: string, updates: Partial<FileUploadState>) => {
      setQueueState((prev) => ({
        ...prev,
        files: prev.files.map((file) =>
          file.id === fileId ? { ...file, ...updates } : file
        ),
      }));
    },
    []
  );

  const validateFile = useCallback(
    async (file: File): Promise<{ valid: boolean; error?: string }> => {
      const isImage =
        file.type.startsWith("image/") && file.type !== "image/gif";
      const isVideo = isVideoFile(file);

      switch (type) {
        case "image":
        case "logo":
        case "assetImage":
          if (!isImage) {
            return {
              valid: false,
              error:
                "Unsupported file format. Please upload PNG, JPEG, or WebP images.",
            };
          }
          if (file.size > MAX_IMAGE_SIZE) {
            return {
              valid: false,
              error: "Exceeds maximum size of 25 MB",
            };
          }
          break;

        case "video":
        case "assetVideo":
          if (!isVideo) {
            return {
              valid: false,
              error:
                "Unsupported file format. Please upload MP4, MOV, or other video formats.",
            };
          }
          if (file.size > MAX_VIDEO_SIZE) {
            return {
              valid: false,
              error: "Exceeds maximum size of 2 GB",
            };
          }
          break;

                case "asset":
                case "listing":
                    if (!isImage && !isVideo) {
                        return {
                            valid: false,
                            error: "Unsupported file format. Please upload images or videos.",
                        };
                    }
                    if (isVideo && file.size > MAX_VIDEO_SIZE) {
                        return {
                            valid: false,
                            error: "Exceeds maximum video size of 2 GB",
                        };
                    }
                    if (isImage && file.size > MAX_IMAGE_SIZE) {
                        return {
                            valid: false,
                            error: "Exceeds maximum image size of 25 MB",
                        };
                    }
                    break;

        default:
          return { valid: false, error: "Unsupported media type" };
      }

      return { valid: true };
    },
    [type]
  );

  const checkIfVideoNeedsProcessing = useCallback(
    async (file: File): Promise<boolean> => {
      if (file.size > 200 * 1024 * 1024) {
        return true;
      }

      return new Promise<boolean>((resolve) => {
        const video = document.createElement("video");
        video.preload = "metadata";

        video.onloadedmetadata = () => {
          if (!video.duration || !video.videoWidth || !video.videoHeight) {
            URL.revokeObjectURL(video.src);
            resolve(true);
            return;
          }

          const isHD =
            (video.videoWidth <= 1920 && video.videoHeight <= 1080) ||
            (video.videoWidth <= 1080 && video.videoHeight <= 1920);

          if (!isHD) {
            URL.revokeObjectURL(video.src);
            resolve(true);
            return;
          }

          const fileExt = file.name.split(".").pop()?.toLowerCase();
          const commonFormats = ["mp4", "mov", "webm"];
          const mightNeedCodecProcessing = !commonFormats.includes(
            fileExt || ""
          );

          URL.revokeObjectURL(video.src);
          resolve(mightNeedCodecProcessing);
        };

        video.onerror = () => {
          URL.revokeObjectURL(video.src);
          resolve(true);
        };

        video.src = URL.createObjectURL(file);
      });
    },
    []
  );

  const getSignedUrl = useCallback(
    async (file: File, filename: string): Promise<string> => {
      const authHeader = await getAuthHeader();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

      let endpoint: string;
      let requestType: string;

      if (
        type === "asset" ||
        type === "assetImage" ||
        type === "assetVideo" ||
        type === "logo"
      ) {
        endpoint = `${backendUrl}/assets/signed-url`;
        requestType = file.type.startsWith("image/") ? "image" : "video";
      } else {
        endpoint = `${backendUrl}/listing/signed-url`;
        requestType = file.type.startsWith("image/") ? "image" : "video";
      }

      const { data } = await axios.post(
        endpoint,
        { filename, contentType: getFileContentType(file), type: requestType },
        { headers: authHeader }
      );

      return data.signedUrl;
    },
    [type]
  );

  const uploadFileToGCS = useCallback(
    async (fileId: string, signedUrl: string): Promise<void> => {
      const currentFileState = getCurrentFileState(fileId);
      if (!currentFileState) {
        throw new Error("File state not found during upload");
      }

      const { file, abortController } = currentFileState;

      const startTime = Date.now();
      let lastTime = startTime;
      let lastLoaded = 0;

      await axios.put(signedUrl, file, {
        headers: { "Content-Type": getFileContentType(file) },
        signal: abortController?.signal,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (!progressEvent.loaded || !progressEvent.total) return;

          const currentTime = Date.now();
          const timeDiff = currentTime - lastTime;
          const loadedDiff = progressEvent.loaded - lastLoaded;

          if (timeDiff > 100) {
            // Update every 100ms
            const uploadSpeed = loadedDiff / (timeDiff / 1000); // bytes per second
            const remainingBytes = progressEvent.total - progressEvent.loaded;
            const timeRemaining =
              uploadSpeed > 0 ? remainingBytes / uploadSpeed : 0;

            updateFileState(fileId, {
              progress: (progressEvent.loaded / progressEvent.total) * 85, // Upload is 80% of total progress
              uploadedBytes: progressEvent.loaded,
              totalBytes: progressEvent.total,
              uploadSpeed,
              timeRemaining,
              lastProgressTime: currentTime,
            });

            lastTime = currentTime;
            lastLoaded = progressEvent.loaded;
          }
        },
      });
    },
    [getCurrentFileState, updateFileState]
  );

  const persistFile = useCallback(
    async (
      fileId: string,
      filename?: string,
      videoData?: { thumbnail?: string; needsConversion?: boolean }
    ): Promise<void> => {
      const currentFileState = getCurrentFileState(fileId);
      if (!currentFileState) {
        throw new Error("File state not found during persistence");
      }

      // Use passed filename or fallback to state filename
      const fileToUse = filename || currentFileState.filename;
      if (!fileToUse) {
        throw new Error(
          "Filename not provided and not found in file state during persistence"
        );
      }

      const authHeader = await getAuthHeader();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

            if (
                type === "image" ||
                (type === "asset" &&
                    currentFileState.file.type.startsWith("image/")) ||
                (type === "listing" &&
                    currentFileState.file.type.startsWith("image/")) ||
                type === "assetImage" ||
                type === "logo"
            ) {
                updateFileState(fileId, {
                    status: UploadStatus.PERSISTING,
                    phase: UploadPhase.PERSISTENCE,
                    progress: 85,
                });

        // Handle image persistence
        const endpoint =
          type === "asset" || type === "assetImage" || type === "logo"
            ? `${backendUrl}/assets/images`
            : `${backendUrl}/listing/images`;

        // Start a fake progress timer from 85 to 99 over 10 seconds while waiting for the backend
        const fakeDurationMs = 10000; // 10 seconds
        const tickMs = 100; // update every 100ms
        const startTs = Date.now();
        const fakeProgressInterval = setInterval(() => {
          const elapsed = Date.now() - startTs;
          const fraction = Math.min(elapsed / fakeDurationMs, 1);
          const target = 85 + (99 - 85) * fraction;
          updateFileState(fileId, {
            progress: Math.min(Math.floor(target), 99),
          });
          if (fraction >= 1) {
            clearInterval(fakeProgressInterval);
          }
        }, tickMs);

                try {
                    await axios.post(
                        endpoint,
                        {
                            listingId:
                                type === "asset" ||
                                type === "assetImage" ||
                                type === "logo"
                                    ? listingType !== ListingType.Promotion
                                        ? null
                                        : listingId
                                    : listingId,
                            listingType:
                                type === "asset" ||
                                type === "assetImage" ||
                                type === "logo"
                                    ? listingType !== ListingType.Promotion
                                        ? null
                                        : listingType
                                    : listingType,
                            filenames: [fileToUse],
                            names: [currentFileState.file.name],
                            assetType:
                                type === "asset" ||
                                type === "assetImage" ||
                                type === "logo"
                                    ? assetType
                                    : undefined,
                        },
                        { headers: authHeader }
                    );
                } catch (error) {
                    clearInterval(fakeProgressInterval);
                    
                    // Check if it's a 410 error (image tag limit exceeded)
                    if (axios.isAxiosError(error) && error.response?.status === 410) {
                        throw new Error("IMAGE_TAG_LIMIT_EXCEEDED");
                    }
                    throw error;
                } finally {
                    clearInterval(fakeProgressInterval);
                }
            } else if (
                type === "video" ||
                (type === "asset" &&
                    isVideoFile(currentFileState.file)) ||
                (type === "listing" &&
                    isVideoFile(currentFileState.file)) ||
                type === "assetVideo"
            ) {
                updateFileState(fileId, {
                    status: UploadStatus.PERSISTING,
                    phase: UploadPhase.PERSISTENCE,
                    progress: 99,
                });

        // Handle video persistence
        const endpoint =
          type === "asset" || type === "assetVideo"
            ? `${backendUrl}/assets/videos`
            : `${backendUrl}/listing/videos/${listingId}`;

        const method =
          type === "asset" || type === "assetVideo" ? "post" : "patch";

        // Extract video metadata
        let videoMetadata: { duration?: number; width?: number; height?: number; aspectRatio?: string; size: number; mimeType: string } | undefined;
        try {
          const metadataUrl = URL.createObjectURL(currentFileState.file);
          const metaVideo = document.createElement("video");
          metaVideo.preload = "metadata";
          await new Promise<void>((resolve) => {
            metaVideo.onloadedmetadata = () => resolve();
            metaVideo.onerror = () => resolve();
            metaVideo.src = metadataUrl;
          });
          const w = metaVideo.videoWidth;
          const h = metaVideo.videoHeight;
          let aspectRatio: string | undefined;
          if (w && h) {
            const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
            const divisor = gcd(w, h);
            aspectRatio = `${w / divisor}:${h / divisor}`;
          }
          videoMetadata = {
            duration: isFinite(metaVideo.duration) ? Math.round(metaVideo.duration) : undefined,
            width: w || undefined,
            height: h || undefined,
            aspectRatio,
            size: currentFileState.file.size,
            mimeType: currentFileState.file.type,
          };
          URL.revokeObjectURL(metadataUrl);
        } catch {
          // Metadata extraction is best-effort
        }

        const requestData = {
          filenames: [fileToUse],
          needsConversion: [
            videoData?.needsConversion ??
              currentFileState.needsConversion ??
              false,
          ],
          thumbnails: [
            videoData?.thumbnail ?? currentFileState.thumbnail ?? null,
          ],
          listingType: listingType || null,
          ...(videoMetadata ? { videoMetadata: [videoMetadata] } : {}),
        };

        await axios[method](endpoint, requestData, {
          headers: authHeader,
        });
      }
    },
    [
      getCurrentFileState,
      updateFileState,
      type,
      listingType,
      listingId,
      assetType,
    ]
  );

  const validateVideoOnBackend = useCallback(
    async (
      fileId: string,
      filename?: string
    ): Promise<VideoValidationResult> => {
      const currentFileState = getCurrentFileState(fileId);
      if (!currentFileState) {
        throw new Error("File state not found during video validation");
      }

      // Use passed filename or fallback to state filename
      const fileToUse = filename || currentFileState.filename;
      if (!fileToUse) {
        throw new Error(
          "Filename not provided and not found in file state during video validation"
        );
      }

      const authHeader = await getAuthHeader();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

      // Start progress simulation from 85 to 99
      const startProgress = 85;
      const endProgress = 99;
      const progressRange = endProgress - startProgress;

      // Estimate time based on file size and previous upload time
      const estimatedTime = Math.max(
        3000, // Minimum 3 seconds
        Math.min(30000, (currentFileState.file.size / (1024 * 1024)) * 1000) // ~1 second per MB, max 30 seconds
      );

      const progressUpdateInterval = 200; // Update every 200ms
      const totalUpdates = estimatedTime / progressUpdateInterval;
      let currentUpdate = 0;

      // Start the progress simulation
      const progressInterval = setInterval(() => {
        currentUpdate++;
        const progressFraction = Math.min(currentUpdate / totalUpdates, 1);

        // Use easing function for more natural progress
        const easedProgress = 1 - Math.pow(1 - progressFraction, 2); // Ease-out quad
        const currentProgress =
          startProgress + progressRange * easedProgress * 0.95; // Go to 95% of range

        updateFileState(fileId, {
          progress: Math.min(currentProgress, endProgress - 1), // Never reach 99 until complete
        });
      }, progressUpdateInterval);

      try {
        const { data } = await axios.post(
          `${backendUrl}/services/check-video`,
          {
            files: [fileToUse],
            names: [currentFileState.file.name],
            type:
              type === "asset" || type === "assetVideo" ? "asset" : "listing",
            listingId:
              type !== "asset" && type !== "assetVideo" ? listingId : undefined,
            listingType:
              type !== "asset" && type !== "assetVideo"
                ? listingType
                : undefined,
          },
          { headers: authHeader }
        );

        // Clear the interval and set final progress
        clearInterval(progressInterval);

        return data[0] as VideoValidationResult;
      } catch (error) {
        clearInterval(progressInterval);
        // MB-1396: a 503/VIDEO_SERVICE_UNAVAILABLE means the video service —
        // not the file — is the problem. Fail the upload with the same copy so
        // the queue row explains itself.
        //
        // Notify ONCE per batch. This runs per file and inside the retry loop,
        // and files finish uploading seconds apart, so a toastId alone is not
        // enough: react-toastify only collapses a duplicate while the original
        // is still on screen, and by the second failure the first has usually
        // auto-closed. The ref is what actually bounds it; the toastId stays as
        // a cheap guard for the simultaneous case.
        if (
          axios.isAxiosError(error) &&
          error.response?.status === 503 &&
          error.response?.data?.message === VIDEO_SERVICE_UNAVAILABLE_CODE
        ) {
          if (!serviceUnavailableNotifiedRef.current) {
            serviceUnavailableNotifiedRef.current = true;
            toast.error(VIDEO_SERVICE_UNAVAILABLE_MESSAGE, {
              toastId: VIDEO_SERVICE_UNAVAILABLE_CODE,
            });
          }
          throw new Error(VIDEO_SERVICE_UNAVAILABLE_MESSAGE);
        }
        throw error;
      }
    },
    [getCurrentFileState, updateFileState, type, listingId, listingType]
  );

  
  
  const shouldRetryProcessUpload = (error: unknown, listingType?: string) => {
   // Retry only for RentcastListing
  if (listingType !== "RentcastListing") return false;

  if (!axios.isAxiosError(error)) return true;

  const status = error.response?.status;

  // Do NOT retry auth or business rule errors
  if (status === 401 || status === 403 || status === 410) {
    return false;
  }

  // Retry network & server errors
  if (!status || status >= 500) {
    return true;
  }

  return false;
};

const processUpload = useCallback(
  async (fileId: string): Promise<void> => {
    // Prevent duplicate processing
    if (processingFiles.current.has(fileId)) {
      return;
    }

    processingFiles.current.add(fileId);

    const startState = getCurrentFileState(fileId);
    const isVideo = startState ? isVideoFile(startState.file) : false;

    setQueueState((prev) => ({
      ...prev,
      activeImageUploads: isVideo
        ? prev.activeImageUploads
        : prev.activeImageUploads + 1,
      activeVideoUploads: isVideo
        ? prev.activeVideoUploads + 1
        : prev.activeVideoUploads,
    }));

    try {
      const currentFileState = getCurrentFileState(fileId);
      if (!currentFileState) {
        throw new Error("File not found in queue");
      }

      // =====================
      // Phase 1: Validation (RUNS ONCE)
      // =====================
      updateFileState(fileId, {
        status: UploadStatus.VALIDATING,
        phase: UploadPhase.VALIDATION,
        progress: 5,
      });

      const validationResult = await validateFile(currentFileState.file);
      if (!validationResult.valid) {
        throw new Error(validationResult.error);
      }

      let needsConversion = false;
      if (isVideoFile(currentFileState.file)) {
        needsConversion = await checkIfVideoNeedsProcessing(
          currentFileState.file
        );
        updateFileState(fileId, { needsConversion });
      }

      // =====================
      // Retry loop starts here
      // =====================
      let attempt = 0;

      while (attempt < MAX_UPLOAD_RETRIES) {
        try {
          attempt++;

          updateFileState(fileId, {
            retryAttempt: attempt,
          });

          // =====================
          // Phase 2: Signed URL
          // =====================
          updateFileState(fileId, {
            status: UploadStatus.GETTING_SIGNED_URL,
            phase: UploadPhase.SIGNED_URL,
            progress: 10,
          });

          const ext = currentFileState.file.name
            .split(".")
            .pop()
            ?.toLowerCase();
          if (!ext) throw new Error("File has no extension");

          let filename: string;
          if (
            type === "asset" ||
            type === "assetImage" ||
            type === "assetVideo" ||
            type === "logo"
          ) {
            if (currentFileState.file.type.startsWith("image/")) {
              filename = `${uuidv4()}.${ext}`;
            } else {
              const base =
                propertyAddress1?.replace(/\s+/g, "_") || "asset_video";
              filename = `${base}-${uuidv4().split("-")[0]}-${Date.now()}.${ext}`;
            }
          } else {
            if (currentFileState.file.type.startsWith("image/")) {
              filename = `${uuidv4()}.${ext}`;
            } else {
              const base =
                propertyAddress1?.replace(/\s+/g, "_") || "video";
              filename = `${base}-${uuidv4().split("-")[0]}-${Date.now()}.${ext}`;
            }
          }

          const signedUrl = await getSignedUrl(
            currentFileState.file,
            filename
          );

          updateFileState(fileId, {
            signedUrl,
            filename,
            progress: 15,
          });

          // =====================
          // Phase 3: Upload to GCS
          // =====================
          updateFileState(fileId, {
            status: UploadStatus.UPLOADING,
            phase: UploadPhase.UPLOAD,
          });

          await uploadFileToGCS(fileId, signedUrl);

          // =====================
          // Phase 4: Backend video validation
          // =====================
          let backendVideoValidation: VideoValidationResult | undefined;

          if (isVideoFile(currentFileState.file)) {
            updateFileState(fileId, {
              status: UploadStatus.PROCESSING,
              phase: UploadPhase.PROCESSING,
              progress: 85,
            });

            backendVideoValidation = customVideoValidator
              ? await customVideoValidator(filename, currentFileState.file.name)
              : await validateVideoOnBackend(fileId, filename);

            if (!backendVideoValidation?.valid) {
              throw new Error(
                backendVideoValidation?.reason ||
                  "Video validation failed"
              );
            }

            updateFileState(fileId, {
              needsConversion: backendVideoValidation.needsConversion,
              thumbnail: backendVideoValidation.thumbnail,
              progress: 99,
            });
          }

          // =====================
          // Phase 5: Persist
          // =====================
          const persistData = isVideoFile(currentFileState.file)
            ? {
                thumbnail: backendVideoValidation?.thumbnail,
                needsConversion: backendVideoValidation?.needsConversion,
              }
            : undefined;

          await persistFile(fileId, filename, persistData);

          // =====================
          // SUCCESS
          // =====================
          updateFileState(fileId, {
            status: UploadStatus.COMPLETED,
            progress: 100,
          });

          setQueueState((prev) => ({
            ...prev,
            completedUploads: prev.completedUploads + 1,
          }));

          onFileComplete?.();
          break; // ✅ exit retry loop
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Upload failed";

          console.error(
            `Upload failed for file ${fileId} (attempt ${attempt}):`,
            errorMessage
          );

          if (
            attempt >= MAX_UPLOAD_RETRIES ||
            !shouldRetryProcessUpload(error, listingType)
          ) {
            if (errorMessage === "IMAGE_TAG_LIMIT_EXCEEDED") {
              onImageTagLimitExceeded?.();
            }

            updateFileState(fileId, {
              status: UploadStatus.FAILED,
              error: errorMessage,
            });

            setQueueState((prev) => ({
              ...prev,
              failedUploads: prev.failedUploads + 1,
            }));

            onFileError?.(fileId, errorMessage);
            break;
          }

          updateFileState(fileId, {
            status: UploadStatus.RETRYING,
            phase: UploadPhase.SIGNED_URL,
            progress: 10,
          });

          toast.info(
            `Upload for "${currentFileState.file.name}" failed, retrying (${attempt} of ${MAX_UPLOAD_RETRIES - 1})...`,
          );

          const delayMs = attempt * 1500;
          await new Promise((res) =>
            setTimeout(res, delayMs)
          );
        }
      }
    } finally {
      processingFiles.current.delete(fileId);

      setQueueState((prev) => ({
        ...prev,
        activeImageUploads: isVideo
          ? prev.activeImageUploads
          : Math.max(0, prev.activeImageUploads - 1),
        activeVideoUploads: isVideo
          ? Math.max(0, prev.activeVideoUploads - 1)
          : prev.activeVideoUploads,
      }));

      setTimeout(() => processQueueRef.current?.(), 100);
    }
  },
  [
    getCurrentFileState,
    updateFileState,
    validateFile,
    checkIfVideoNeedsProcessing,
    getSignedUrl,
    uploadFileToGCS,
    validateVideoOnBackend,
    customVideoValidator,
    persistFile,
    onFileComplete,
    onFileError,
    onImageTagLimitExceeded,
    type,
    propertyAddress1,
    listingType,
  ]
);

  const processQueue = useCallback(() => {
    // Clear any existing timeout to prevent duplicate processing
    if (processQueueTimeoutRef.current) {
      clearTimeout(processQueueTimeoutRef.current);
    }

    processQueueTimeoutRef.current = setTimeout(() => {
      const pendingFiles = queueState.files.filter(
        (f) => f.status === UploadStatus.PENDING
      );

      if (pendingFiles.length === 0) return;

      // Separate pending files by type
      const pendingImages = pendingFiles.filter((f) => !isVideoFile(f.file));
      const pendingVideos = pendingFiles.filter((f) => isVideoFile(f.file));

      // Calculate how many of each type we can start
      const imagesSlotsAvailable = Math.max(
        0,
        config.maxConcurrentImageUploads - queueState.activeImageUploads
      );
      const videoSlotsAvailable = Math.max(
        0,
        config.maxConcurrentVideoUploads - queueState.activeVideoUploads
      );

      // Start image uploads
      const imagesToStart = Math.min(
        imagesSlotsAvailable,
        pendingImages.length
      );
      for (let i = 0; i < imagesToStart; i++) {
        const fileToProcess = pendingImages[i];
        if (fileToProcess && !processingFiles.current.has(fileToProcess.id)) {
          processUpload(fileToProcess.id);
        }
      }

      // Start video uploads
      const videosToStart = Math.min(videoSlotsAvailable, pendingVideos.length);
      for (let i = 0; i < videosToStart; i++) {
        const fileToProcess = pendingVideos[i];
        if (fileToProcess && !processingFiles.current.has(fileToProcess.id)) {
          processUpload(fileToProcess.id);
        }
      }
    }, 50);
  }, [
    config.maxConcurrentImageUploads,
    config.maxConcurrentVideoUploads,
    processUpload,
    queueState.files,
    queueState.activeImageUploads,
    queueState.activeVideoUploads,
  ]);

  // Update the ref whenever processQueue changes
  useEffect(() => {
    processQueueRef.current = processQueue;
  }, [processQueue]);

  const addFiles = useCallback(
    (files: File[]) => {
      onUploadStart?.();
      serviceUnavailableNotifiedRef.current = false;

      const newFileStates: FileUploadState[] = files.map((file) => ({
        id: uuidv4(),
        file,
        preview: URL.createObjectURL(file),
        status: UploadStatus.PENDING,
        progress: 0,
        phase: UploadPhase.VALIDATION,
        uploadedBytes: 0,
        totalBytes: file.size,
        uploadSpeed: 0,
        timeRemaining: 0,
        retryCount: 0,
        abortController: new AbortController(),
        startTime: Date.now(),
      }));

      setQueueState((prev) => ({
        ...prev,
        files: [...prev.files, ...newFileStates],
      }));

      // Start processing after state update
      setTimeout(() => processQueueRef.current?.(), 100);
    },
    [onUploadStart]
  );

  // Add a file directly as FAILED (pre-upload validation failure) so it shows
  // in the upload queue UI instead of a toast.
  const addFailedFile = useCallback((file: File, error: string) => {
    const failedState: FileUploadState = {
      id: uuidv4(),
      file,
      preview: URL.createObjectURL(file),
      status: UploadStatus.FAILED,
      progress: 0,
      phase: UploadPhase.VALIDATION,
      totalBytes: file.size,
      uploadedBytes: 0,
      retryCount: 0,
      error,
    };
    setQueueState((prev) => ({
      ...prev,
      files: [...prev.files, failedState],
      failedUploads: prev.failedUploads + 1,
    }));
  }, []);

  const removeFile = useCallback(async (fileId: string) => {
    const fileState = queueState.files.find((f) => f.id === fileId);
    if (!fileState) return;

    // If file was successfully uploaded (completed status), delete from backend
    if (fileState.status === UploadStatus.COMPLETED && fileState.filename && listingId) {
      try {
        const authHeader = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const isVideo = isVideoFile(fileState.file);

        // Fetch listing to get the ID of the uploaded file
        const listingResponse = await axios.get(
          `${backendUrl}/listing/${listingId}`,
          { headers: authHeader }
        );

        if (listingResponse.data) {
          const listing = listingResponse.data;
          
          if (isVideo) {
            // Find video by filename match
            const video = listing.videos?.find(
              (v: ListingVideos) => v.url?.includes(fileState.filename!) || v.name === fileState.file.name
            );
            
            if (video?.id) {
              await axios.delete(`${backendUrl}/listing/videos/`, {
                headers: authHeader,
                data: {
                  listingId: listingId,
                  listingType: listingType,
                  videos: [video.id],
                },
              });
              // Refresh listing data after successful deletion
              onUploadComplete?.();
            }
          } else {
            // Find image by filename match - check name and id fields first
            const image = listing.images?.find(
              (img: Images) => {
                // Primary: Check if name matches the filename directly
                if (img.name === fileState.filename) return true;
                
                // Secondary: Check if id matches filename (with or without extension)
                const filenameWithoutExt = fileState.filename?.replace(/\.[^/.]+$/, "");
                if (img.id === filenameWithoutExt || img.id === fileState.filename) return true;
                
                // Tertiary: Check if any URL contains the filename (for signed URLs)
                if (img.url?.includes(fileState.filename!) || 
                    img.signedUrl?.includes(fileState.filename!) ||
                    img.thumbnail?.includes(fileState.filename!) ||
                    img.slideshow?.includes(fileState.filename!)) return true;
                
                // Last resort: Match by original file name
                if (img.name === fileState.file.name) return true;
                
                return false;
              }
            );
            
            if (image?.id) {
              await axios.delete(`${backendUrl}/listing/images/`, {
                headers: authHeader,
                data: {
                  listingId: listingId,
                  listingType: listingType,
                  imageIds: [image.id],
                },
              });
              // Refresh listing data after successful deletion
              onUploadComplete?.();
            }
          }
        }
      } catch (error) {
        console.error("Error deleting file from backend:", error);
        // Continue with local removal even if backend deletion fails
      }
    }

    setQueueState((prev) => {
      const fileState = prev.files.find((f) => f.id === fileId);
      if (!fileState) return prev;

      // Cancel upload if in progress
      if (
        fileState.abortController &&
        !fileState.abortController.signal.aborted
      ) {
        fileState.abortController.abort();
      }

      // Clean up preview URL
      URL.revokeObjectURL(fileState.preview);

      const wasProcessing = processingFiles.current.has(fileId);
      processingFiles.current.delete(fileId);

      // Decrement appropriate counter if file was being processed
      const isVideo = isVideoFile(fileState.file);
      return {
        ...prev,
        files: prev.files.filter((f) => f.id !== fileId),
        activeImageUploads:
          wasProcessing && !isVideo
            ? Math.max(0, prev.activeImageUploads - 1)
            : prev.activeImageUploads,
        activeVideoUploads:
          wasProcessing && isVideo
            ? Math.max(0, prev.activeVideoUploads - 1)
            : prev.activeVideoUploads,
      };
    });
  }, [queueState.files, listingId, listingType, onUploadComplete]);

  const retryFile = useCallback(
    (fileId: string) => {
      const currentFileState = getCurrentFileState(fileId);
      if (!currentFileState || currentFileState.retryCount >= config.maxRetries)
        return;

      // Remove from processing set if it exists
      processingFiles.current.delete(fileId);

      updateFileState(fileId, {
        status: UploadStatus.PENDING,
        progress: 0,
        phase: UploadPhase.VALIDATION,
        error: undefined,
        retryCount: currentFileState.retryCount + 1,
        abortController: new AbortController(),
        // Reset upload-specific fields
        signedUrl: undefined,
        filename: undefined,
        uploadedBytes: 0,
        uploadSpeed: 0,
        timeRemaining: 0,
        needsConversion: undefined,
        thumbnail: undefined,
      });

      // Process queue after a delay
      setTimeout(
        () => processQueueRef.current?.(),
        config.retryDelayBase * Math.pow(2, currentFileState.retryCount)
      );
    },
    [
      getCurrentFileState,
      config.maxRetries,
      config.retryDelayBase,
      updateFileState,
    ]
  );

  const cancelFile = useCallback(
    (fileId: string) => {
      const fileState = getCurrentFileState(fileId);
      if (!fileState) return;

      if (
        fileState.abortController &&
        !fileState.abortController.signal.aborted
      ) {
        fileState.abortController.abort();
      }

      updateFileState(fileId, {
        status: UploadStatus.CANCELLED,
      });

      processingFiles.current.delete(fileId);

      // Decrement the appropriate counter based on file type
      const isVideo = isVideoFile(fileState.file);
      setQueueState((prev) => ({
        ...prev,
        activeImageUploads: isVideo
          ? prev.activeImageUploads
          : Math.max(0, prev.activeImageUploads - 1),
        activeVideoUploads: isVideo
          ? Math.max(0, prev.activeVideoUploads - 1)
          : prev.activeVideoUploads,
      }));
    },
    [getCurrentFileState, updateFileState]
  );

  const clearCompleted = useCallback(async () => {
    // Get completed files that were successfully uploaded
    const completedSuccessfulFiles = queueState.files.filter(
      (f) => f.status === UploadStatus.COMPLETED && f.filename
    );

    // Delete completed files from backend if there's a listingId
    if (completedSuccessfulFiles.length > 0 && listingId) {
      try {
        const authHeader = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

        // Fetch listing to get IDs of uploaded files
        const listingResponse = await axios.get(
          `${backendUrl}/listing/${listingId}`,
          { headers: authHeader }
        );

        if (listingResponse.data) {
          const listing = listingResponse.data;
          
          // Group files by type
          const videoFiles = completedSuccessfulFiles.filter((f) =>
            isVideoFile(f.file)
          );
          const imageFiles = completedSuccessfulFiles.filter((f) =>
            f.file.type.startsWith("image/")
          );

          const deletePromises = [];

          // Delete videos
          if (videoFiles.length > 0) {
            const videoIds = videoFiles
              .map((vf) => {
                const video = listing.videos?.find(
                  (v: ListingVideos) => v.url?.includes(vf.filename!) || v.name === vf.file.name
                );
                return video?.id;
              })
              .filter(Boolean);

            if (videoIds.length > 0) {
              deletePromises.push(
                axios.delete(`${backendUrl}/listing/videos/`, {
                  headers: authHeader,
                  data: {
                    listingId: listingId,
                    listingType: listingType,
                    videos: videoIds,
                  },
                })
              );
            }
          }

          // Delete images
          if (imageFiles.length > 0) {
            const imageIds = imageFiles
              .map((imgF) => {
                const image = listing.images?.find(
                  (img: Images) => {
                    // Primary: Check if name matches the filename directly
                    if (img.name === imgF.filename) return true;
                    
                    // Secondary: Check if id matches filename (with or without extension)
                    const filenameWithoutExt = imgF.filename?.replace(/\.[^/.]+$/, "");
                    if (img.id === filenameWithoutExt || img.id === imgF.filename) return true;
                    
                    // Tertiary: Check if any URL contains the filename
                    if (img.url?.includes(imgF.filename!) || 
                        img.signedUrl?.includes(imgF.filename!) ||
                        img.thumbnail?.includes(imgF.filename!) ||
                        img.slideshow?.includes(imgF.filename!)) return true;
                    
                    // Last resort: Match by original file name
                    if (img.name === imgF.file.name) return true;
                    
                    return false;
                  }
                );
                return image?.id;
              })
              .filter(Boolean);

            if (imageIds.length > 0) {
              deletePromises.push(
                axios.delete(`${backendUrl}/listing/images/`, {
                  headers: authHeader,
                  data: {
                    listingId: listingId,
                    listingType: listingType,
                    imageIds: imageIds,
                  },
                })
              );
            }
          }

          await Promise.all(deletePromises);
          
          // Refresh listing data after successful batch deletion
          if (deletePromises.length > 0) {
            onUploadComplete?.();
          }
        }
      } catch (error) {
        console.error("Error deleting files from backend:", error);
        // Continue with local clearing even if backend deletion fails
      }
    }

    setQueueState((prev) => {
      const completedFiles = prev.files.filter(
        (f) =>
          f.status === UploadStatus.COMPLETED ||
          f.status === UploadStatus.FAILED ||
          f.status === UploadStatus.CANCELLED
      );

      completedFiles.forEach((file) => {
        URL.revokeObjectURL(file.preview);
        processingFiles.current.delete(file.id);
      });

      return {
        ...prev,
        files: prev.files.filter(
          (f) =>
            f.status !== UploadStatus.COMPLETED &&
            f.status !== UploadStatus.FAILED &&
            f.status !== UploadStatus.CANCELLED
        ),
        completedUploads: 0,
        failedUploads: 0,
      };
    });
    // Reset the completion notification flag so next batch can notify
    completionNotifiedRef.current = false;
  }, [queueState.files, listingId, listingType, onUploadComplete]);

  // Check if all uploads are complete and refresh media list
  useEffect(() => {
    const hasFiles = queueState.files.length > 0;
    const allComplete =
      hasFiles &&
      queueState.files.every(
        (f) =>
          f.status === UploadStatus.COMPLETED ||
          f.status === UploadStatus.FAILED ||
          f.status === UploadStatus.CANCELLED
      );

    const noActiveUploads = 
      queueState.activeImageUploads === 0 && 
      queueState.activeVideoUploads === 0;

    // Only call onUploadComplete when:
    // 1. All files are done (completed/failed/cancelled)
    // 2. No active uploads are running
    // 3. We have at least one successful upload
    // 4. We haven't notified completion yet for this batch
    // This refreshes the media list so new uploads appear for selection
    if (
      allComplete && 
      noActiveUploads && 
      queueState.completedUploads > 0 &&
      !completionNotifiedRef.current
    ) {
      completionNotifiedRef.current = true;
      onUploadComplete?.();
    }

    // Reset notification flag when new files are added
    if (!allComplete && completionNotifiedRef.current) {
      completionNotifiedRef.current = false;
    }
  }, [
    queueState.files,
    queueState.completedUploads,
    queueState.activeImageUploads,
    queueState.activeVideoUploads,
    onUploadComplete,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    const processingSet = processingFiles.current;

    return () => {
      // Clear any outstanding timeout
      if (processQueueTimeoutRef.current) {
        clearTimeout(processQueueTimeoutRef.current);
      }

      // Abort and revoke previews for the latest known files
      filesRef.current.forEach((file) => {
        try {
          file.abortController?.abort();
        } catch {
          // Ignore abort errors on cleanup
        }
        try {
          URL.revokeObjectURL(file.preview);
        } catch {
          // Ignore revoke errors on cleanup
        }
      });

      // Clear processing set
      processingSet.clear();
    };
  }, []);

  // Helper to check if there are active uploads
  const hasActiveUploads =
    queueState.activeImageUploads > 0 || queueState.activeVideoUploads > 0;

  // Method to cancel all active uploads
  const cancelAllUploads = useCallback(() => {
    filesRef.current.forEach((file) => {
      if (file.status === UploadStatus.UPLOADING || file.status === UploadStatus.PENDING) {
        try {
          file.abortController?.abort();
        } catch (error) {
          console.error(`Failed to abort ${file.file.name}:`, error);
        }
      }
    });
    // Clear the queue state
    setQueueState({
      files: [],
      activeImageUploads: 0,
      activeVideoUploads: 0,
      completedUploads: 0,
      failedUploads: 0,
      totalProgress: 0,
    });
    processingFiles.current.clear();
  }, []);

  return {
    queueState,
    addFiles,
    addFailedFile,
    removeFile,
    retryFile,
    cancelFile,
    clearCompleted,
    config,
    hasActiveUploads,
    cancelAllUploads,
  };
};
